var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { ELocale } from "../../types/i18n.types";
import { stringToSnakeCase } from "../string.utils";
import { capitalize } from "../text/text.utils";
import MESSAGES_MERGED from "./locale/i18n.json";
import MESSAGES_OVERRIDES from "./locale/i18n.overrides.json";
// Merge base (Lokalise) with overrides — overrides take priority
// _meta is reserved for override metadata (timestamps, author) — skip it
var MERGED = __assign(__assign({}, MESSAGES_MERGED), MESSAGES_OVERRIDES);
var MESSAGES_EN = {};
var MESSAGES_NL = {};
var MESSAGES_FR = {};
Object.keys(MERGED).forEach(function (key) {
    if (key === "_meta")
        return;
    // @ts-ignore
    var enMessage = MERGED[key][0];
    if (enMessage) {
        MESSAGES_EN[key] = enMessage;
    }
    // @ts-ignore
    var nlMessage = MERGED[key][1];
    if (nlMessage) {
        MESSAGES_NL[key] = nlMessage;
    }
    // @ts-ignore
    var frMessage = MERGED[key][2];
    if (frMessage) {
        MESSAGES_FR[key] = frMessage;
    }
});
var DEFAULT_LOCALE = ELocale.EN;
var MESSAGES = (_a = {},
    _a[ELocale.EN] = MESSAGES_EN,
    _a[ELocale.NL] = MESSAGES_NL,
    _a[ELocale.FR] = MESSAGES_FR,
    _a[ELocale.INTERNAL] = {},
    _a);
var locale = DEFAULT_LOCALE;
export var getLocale = function () { return locale; };
var rawLocale = DEFAULT_LOCALE;
export var getRawLocale = function () { return rawLocale; };
export var setMappedLocale = function (newLocale) {
    locale = newLocale;
};
export var setLocale = function (newLocale) {
    rawLocale = newLocale;
    if (newLocale.toLowerCase().includes("nl")) {
        locale = ELocale.NL;
        return;
    }
    if (newLocale.toLowerCase().includes("fr")) {
        locale = ELocale.FR;
        return;
    }
    if (newLocale === ELocale.INTERNAL) {
        locale = ELocale.INTERNAL;
        return;
    }
    locale = ELocale.EN;
};
var getMessagesForLocale = function (locale) {
    return MESSAGES[locale];
};
var getMessageForId = function (id, pluralIndex, customLocale) {
    if (pluralIndex === void 0) { pluralIndex = 0; }
    var messages = getMessagesForLocale(customLocale || locale || DEFAULT_LOCALE);
    var message = messages[id];
    // Fallback if key not translated
    if (!message) {
        return id;
    }
    // Plural
    if (typeof message === "object" && message !== null) {
        var pluralStrings = ["zero", "one", "many"];
        // INFO: Get absolute value to fix negative indexes
        var pluralString = pluralStrings[Math.min(pluralStrings.length - 1, Math.abs(pluralIndex))];
        message = message[pluralString];
        if (!message) {
            return id;
        }
    }
    if (typeof message !== "string") {
        return id;
    }
    return message;
};
export var getLocalizedText = function (id, values, pluralIndex, customLocale) {
    if (values === void 0) { values = {}; }
    if (pluralIndex === void 0) { pluralIndex = 0; }
    var message = getMessageForId(id, pluralIndex, customLocale);
    // Replace translation for values
    if (values) {
        Object.keys(values).forEach(function (value) {
            var regex = new RegExp("{{".concat(value, "}}"), "g");
            message = message.replace(regex, values[value]);
        });
    }
    return message;
};
/**
 * Label for an enum value that may not be there, for summary rendering.
 *
 * A `formatSummary` callback cannot assume its value exists: two of the four
 * summary implementations (this package's FormSummary and summary.utils'
 * mapField) only honour the STEP-level `showIf` and then format every field
 * with data, hidden ones included - and a field hidden while its step was
 * submitted has no stored value at all. Returns undefined so the summary simply
 * shows nothing, rather than throwing or printing a raw key.
 */
export var getOptionalLocalizedEnumText = function (params) {
    var prefix = params.prefix, value = params.value, customLocale = params.customLocale;
    return value
        ? getLocalizedText("".concat(prefix, ".").concat(value.toLowerCase()), {}, 0, customLocale)
        : undefined;
};
// Feedback messages
var sanitize = function (input) { return capitalize(input.toLowerCase()); };
var getReadableModel = function (modelType) {
    return getLocalizedText("system.model.".concat(stringToSnakeCase(modelType)).toLowerCase());
};
export var getGeneralCreateSuccessText = function (type) {
    return sanitize(getLocalizedText("system.create.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralCreateSuccessGoToDetailText = function (type) {
    return sanitize(getLocalizedText("system.create.success.cta_detail", {
        type: getReadableModel(type),
    }));
};
export var getGeneralCreateFailureText = function (type) {
    return sanitize(getLocalizedText("system.create.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUpdateSuccessText = function (type) {
    return sanitize(getLocalizedText("system.update.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUpdateFailureText = function (type) {
    return sanitize(getLocalizedText("system.update.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralRemoveSuccessText = function (type) {
    return sanitize(getLocalizedText("system.delete.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralRemoveFailureText = function (type) {
    return sanitize(getLocalizedText("system.delete.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralArchiveSuccessText = function (type) {
    return sanitize(getLocalizedText("system.archive.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralArchiveFailureText = function (type) {
    return sanitize(getLocalizedText("system.archive.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUnarchiveSuccessText = function (type) {
    return sanitize(getLocalizedText("system.unarchive.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUnarchiveFailureText = function (type) {
    return sanitize(getLocalizedText("system.unarchive.failure", {
        type: getReadableModel(type),
    }));
};
